import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, ArrowRight, BookOpen, Sparkles, Users } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  guidesData,
  guideCategories,
  searchGuides,
  getGuidesByCategory
} from '../data/guidesData';

const GuidesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [filteredGuides, setFilteredGuides] = useState(guidesData);

  useEffect(() => {
    let guides = guidesData;

    if (selectedCategory !== 'All') {
      guides = getGuidesByCategory(selectedCategory);
    }

    if (searchQuery) {
      guides = searchGuides(searchQuery);
    }

    setFilteredGuides(guides);
  }, [searchQuery, selectedCategory]);

  const breadcrumbItems = [
    { label: 'Guides', href: null }
  ];

  const guidesSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Sortio File Organization Guides",
    "description": "Step-by-step guides for organizing files by profession and use case. Learn how to structure documents for legal, creative, finance, and business workflows.",
    "url": "https://www.getsortio.com/guides",
    "publisher": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    },
    "numberOfItems": guidesData.length,
    "hasPart": guidesData.map(guide => ({
      "@type": "HowTo",
      "name": guide.title,
      "description": guide.shortDescription,
      "url": `https://www.getsortio.com/guides/${guide.slug}`
    }))
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#121212" }}>
      <SEOHead
        title="File Organization Guides | Sortio - Industry-Specific Workflows"
        description="Step-by-step guides for organizing files by profession. Learn best practices for legal, creative, finance, and business document management with Sortio."
        canonical="https://www.getsortio.com/guides"
        schema={guidesSchema}
        keywords="file organization guides, document management tutorials, legal file organization, creative asset management, finance document organization"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center p-1.5 rounded-full bg-sortio-secondary/10 px-4 py-1.5">
              <BookOpen className="mr-2 text-sortio-secondary" size={14} />
              <span className="text-xs font-medium uppercase tracking-wider text-sortio-secondary">Organization Guides</span>
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-semibold mb-6 tracking-tight text-apple-50">
            File Organization <span className="apple-gradient-text">Guides</span>
          </h1>
          <p className="text-apple-300 text-xl max-w-3xl mx-auto font-light leading-relaxed">
            Step-by-step workflows for organizing files in your industry. From legal case files to creative assets, find the perfect structure for your needs.
          </p>
          <div className="mt-8 text-apple-400">
            <span className="font-medium">{filteredGuides.length}</span> guides available
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-12 space-y-6">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-apple-400" size={20} />
            <input
              type="text"
              placeholder="Search guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-apple-700/40 border border-apple-600/30 rounded-2xl text-apple-50 placeholder-apple-400 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all duration-300"
            />
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Filter className="text-apple-400" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-apple-700/40 border border-apple-600/30 rounded-xl px-4 py-2 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50"
              >
                {guideCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {(searchQuery || selectedCategory !== 'All') && (
                <button
                  onClick={clearFilters}
                  className="text-sortio-primary hover:text-sortio-primary/80 text-sm font-medium transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 bg-apple-700/40 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'grid'
                    ? 'bg-sortio-primary text-white'
                    : 'text-apple-400 hover:text-apple-50'
                }`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'list'
                    ? 'bg-sortio-primary text-white'
                    : 'text-apple-400 hover:text-apple-50'
                }`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Guides Display */}
        {filteredGuides.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="mx-auto mb-4 text-apple-400" size={48} />
            <h3 className="text-xl font-medium text-apple-50 mb-2">No guides found</h3>
            <p className="text-apple-400">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className={
            viewMode === 'grid'
              ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }>
            {filteredGuides.map((guide) => (
              <Link
                key={guide.id}
                to={`/guides/${guide.slug}`}
                className={`group block ${
                  viewMode === 'grid'
                    ? 'bg-apple-700/40 backdrop-blur-md p-6 rounded-2xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 hover:translate-y-[-4px]'
                    : 'bg-apple-700/40 backdrop-blur-md p-4 rounded-xl border border-apple-600/30 hover:border-sortio-primary/50 transition-all duration-300 flex items-center justify-between'
                }`}
              >
                <div className={viewMode === 'grid' ? '' : 'flex-1'}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium text-apple-50 group-hover:text-sortio-primary transition-colors">
                      {guide.title}
                    </h3>
                    {viewMode === 'grid' && (
                      <ArrowRight className="text-apple-400 group-hover:text-sortio-primary transition-colors" size={16} />
                    )}
                  </div>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-block px-2 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg">
                      {guide.category}
                    </span>
                    <span className="text-apple-400 text-xs">
                      {guide.steps.length} steps
                    </span>
                  </div>
                  <p className="text-apple-300 text-sm leading-relaxed mb-3">
                    {guide.shortDescription}
                  </p>
                  {viewMode === 'grid' && (
                    <div className="flex items-center text-apple-400 text-xs">
                      <Users size={12} className="mr-1" />
                      {guide.audience}
                    </div>
                  )}
                </div>
                {viewMode === 'list' && (
                  <ArrowRight className="text-apple-400 group-hover:text-sortio-primary transition-colors ml-4" size={16} />
                )}
              </Link>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-24 text-center">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-3xl p-8 border border-sortio-primary/20">
            <Sparkles className="mx-auto mb-4 text-sortio-primary" size={32} />
            <h3 className="text-2xl font-semibold text-apple-50 mb-4">
              Ready to Get Organized?
            </h3>
            <p className="text-apple-300 mb-6 max-w-2xl mx-auto">
              Put these guides into practice with Sortio's AI-powered file organization.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-8 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Try Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidesPage;
